import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  SortingFn,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState, useRef } from 'react';
import { rankItem } from '@tanstack/match-sorter-utils';
import { FundFailureSummary } from '../types/dashboard.types';
import { getComplianceSubcategoryColor } from '../../../../utilities/colorUtilities';
import { Link } from 'react-router-dom';

const getPercentageColorClass = (
  percentage: number,
  type: 'fail' | 'alert' | 'pass',
) => {
  const ratePct = percentage * 100;

  switch (type) {
    case 'fail':
      if (ratePct >= 50) return 'text-fail font-semibold';
      if (ratePct >= 25) return 'text-orange-600 font-medium';
      return 'text-gray-600';
    case 'alert':
      if (ratePct >= 50) return 'text-alert font-semibold';
      if (ratePct >= 25) return 'text-orange-500 font-medium';
      return 'text-gray-600';
    case 'pass':
      if (ratePct >= 90) return 'text-pass font-semibold';
      if (ratePct >= 75) return 'text-green-600 font-medium';
      return 'text-gray-600';
    default:
      return 'text-gray-600';
  }
};
import { useChartView } from '../DashboardContext';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import SortIcon from '@mui/icons-material/Sort';
import { Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { sortFundNames } from '../../../../utilities/customSorting';
import InfoIcon from '../../../ui/InfoIcon';
import { percentageToNdecialPlaces } from '../../../../utilities/numberFormatters';
import { sectionIdFromFundType } from '../../../../types/server-data/ClientConfig';

function TruncateWithTooltip({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const isOverflowing = () => {
    const el = ref.current;
    if (!el) return false;
    return el.scrollWidth > el.clientWidth;
  };

  return (
    <Tooltip
      title={text}
      placement="top"
      arrow
      disableHoverListener={!isOverflowing()}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'white',
            color: '#111827',
            padding: '0.5rem 0.625rem',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          },
        },
        arrow: {
          sx: {
            color: 'white',
            '&::before': {
              border: '1px solid #e5e7eb',
              boxSizing: 'border-box',
            },
          },
        },
      }}
    >
      <span ref={ref} className={className}>
        {text}
      </span>
    </Tooltip>
  );
}

// Consider readding the date for good navigation
const fuzzyGlobalFilter = (
  row: any,
  _columnId: string,
  filterValue: string,
) => {
  const { original } = row;

  const subcategories = original.subcategories?.join(', ') ?? '';

  // Combine only fund_name, fund_name_full, and subcategories for search
  const searchString = [
    original.fund_name,
    original.fund_name_full,
    subcategories,
  ].join(' ');

  const result = rankItem(searchString, filterValue, {
    threshold: 4,
  });

  return result.passed;
};

export default function ComplianceTable({
  data,
  daysInScope,
  searchTerm,
  pageSize,
}: {
  data: FundFailureSummary[]; // aggregated]
  daysInScope: number; // number of days in scope for the data
  searchTerm?: string; // optional search term for filtering
  pageSize?: number; // optional page size, defaults to 10
}) {
  const { setHoveredFundId } = useChartView();
  const containerRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const rowHeightRef = useRef<number | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const bodyHeightRef = useRef<number | null>(null);
  const headerHeightRef = useRef<number>(48);

  const [globalFilter, setGlobalFilter] = useState(searchTerm);
  const [sorting, setSorting] = useState([
    { id: 'fail_rate_percent', desc: true },
    { id: 'alert_rate_percent', desc: true },
  ] as any);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: pageSize || 10,
  });

  // Calculate optimal page size based on container height
  useEffect(() => {
    const calculateOptimalPageSize = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const viewportBottomPadding = 56; // reserve a little space from viewport bottom
      const viewportAvailable = Math.max(
        0,
        window.innerHeight - rect.top - viewportBottomPadding,
      );

      const measuredHeader = (
        bodyRef.current?.querySelector('thead') as HTMLElement | null
      )?.offsetHeight;
      const measuredPagination = paginationRef.current?.offsetHeight;
      const headerH =
        measuredHeader && measuredHeader > 0 ? measuredHeader : 48;
      const paginationH =
        measuredPagination && measuredPagination > 0 ? measuredPagination : 40;
      headerHeightRef.current = headerH;

      // Try to measure an actual row height once; fallback to 41
      const sampleRow = bodyRef.current?.querySelector(
        'tbody tr',
      ) as HTMLElement | null;
      const measuredRowHeight = sampleRow?.getBoundingClientRect().height;
      if (measuredRowHeight && measuredRowHeight > 0) {
        rowHeightRef.current = measuredRowHeight;
      }
      const rowH = Math.max(36, Math.round(rowHeightRef.current ?? 41));

      const availableForRows = Math.max(
        0,
        viewportAvailable - headerH - paginationH,
      );
      const optimalRows = Math.floor(availableForRows / rowH);

      const maxPageSize = pageSize || 10;
      const clamped = Math.max(4, Math.min(optimalRows, maxPageSize));

      // Cache the body height we will render, so it's stable even when there are zero rows
      bodyHeightRef.current = clamped * rowH;

      setPagination((prev) => {
        if (prev.pageSize === clamped && prev.pageIndex === 0) return prev;
        return { pageIndex: prev.pageIndex, pageSize: clamped }; // Keep current page index
      });
    };

    calculateOptimalPageSize();

    // Recalculate on window resize with debounce
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateOptimalPageSize, 100);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Use ResizeObserver to detect container size changes (important for modals)
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateOptimalPageSize, 100);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    if (paginationRef.current) resizeObserver.observe(paginationRef.current);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, [pageSize]);

  // Update pagination when pageSize prop changes
  useEffect(() => {
    setPagination((prev) => ({
      pageIndex: prev.pageIndex, // Keep current page index instead of resetting to 0
      pageSize: pageSize || 10,
    }));
  }, [pageSize]);

  useEffect(() => {
    setGlobalFilter(searchTerm || '');
  }, [searchTerm]);

  const columns = useMemo<ColumnDef<FundFailureSummary>[]>(
    () => [
      {
        accessorKey: 'fund_name',
        header: 'Fund ID',
        size: 120,
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue(columnId) as string;
          const b = rowB.getValue(columnId) as string;
          return sortFundNames(a, b);
        },
      },
      {
        accessorKey: 'fund_name_full',
        header: 'Fund Name',
        size: 200,
        cell: ({ getValue }) => {
          const full = (getValue() as string) || '';
          return (
            <TruncateWithTooltip
              text={full}
              className="block max-w-[clamp(8rem,15vw,40rem)] truncate"
            />
          );
        },
      },
      {
        accessorKey: 'subcategories',
        header: 'Sub-Categories',
        size: 120,
        cell: ({ getValue }) => {
          const subcategories = getValue() as string[];
          const count = subcategories.length;

          const compClass = (sub: string) =>
            `rounded-full px-2 py-1 text-xs font-medium text-black outline outline-1 ${getComplianceSubcategoryColor(
              sub,
            )}`;

          return (
            <Tooltip
              title={
                <div className="flex max-w-xs flex-col space-y-2">
                  {subcategories.map((sub, idx) => (
                    <span key={idx} className={compClass(sub)}>
                      {sub}
                    </span>
                  ))}
                </div>
              }
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'white',
                    padding: '0.5rem',
                    border: '1px solid #eaecef',
                    borderRadius: '0.5rem',
                  },
                },
              }}
            >
              <span className="cursor-pointer rounded-full bg-gray-200 px-4 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-300">
                +{count}
              </span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'fail_count',
        header: 'Fails',
        size: 80,
        cell: ({ getValue }) => getValue() as number,
      },
      {
        accessorKey: 'alert_count',
        header: 'Alerts',
        size: 80,
        cell: ({ getValue }) => getValue() as number,
      },
      {
        accessorKey: 'pass_count',
        header: 'Passes',
        size: 80,
        cell: ({ getValue }) => getValue() as number,
      },
      {
        accessorKey: 'total_runs',
        header: () => (
          <span className="flex items-center justify-end gap-1">
            Runs
            <InfoIcon tooltip="Total number of processing runs for this fund." />
          </span>
        ),
        size: 100,
        cell: ({ getValue }) => getValue() as number,
      },
      {
        id: 'fail_rate_percent',
        header: 'Fail Rate',
        size: 100,
        accessorKey: 'fail_rate_percent',
        cell: ({ row, getValue }) => {
          const value =
            row.original.fail_rate_percent ?? Number(getValue()) ?? 0;
          const colorClass = getPercentageColorClass(
            Number(value) || 0,
            'fail',
          );
          return (
            <span className={colorClass}>
              {percentageToNdecialPlaces(Number(value) || 0, 0)}
            </span>
          );
        },
      },
      {
        id: 'alert_rate_percent',
        header: 'Alert Rate',
        size: 100,
        accessorKey: 'alert_rate_percent',
        cell: ({ row, getValue }) => {
          const value =
            row.original.alert_rate_percent ?? Number(getValue()) ?? 0;
          const colorClass = getPercentageColorClass(
            Number(value) || 0,
            'alert',
          );
          return (
            <span className={colorClass}>
              {percentageToNdecialPlaces(Number(value) || 0, 0)}
            </span>
          );
        },
      },
      {
        id: 'pass_rate_percent',
        header: 'Pass Rate',
        size: 100,
        accessorKey: 'pass_rate_percent',
        cell: ({ row, getValue }) => {
          const value =
            row.original.pass_rate_percent ?? Number(getValue()) ?? 0;
          const colorClass = getPercentageColorClass(
            Number(value) || 0,
            'pass',
          );
          return (
            <span className={colorClass}>
              {percentageToNdecialPlaces(Number(value) || 0, 0)}
            </span>
          );
        },
      },
      {
        accessorKey: 'more_info',
        header: 'Fail Details',
        size: 80,
        cell: ({ row }) => {
          const { fund_name, regulation_id } = row.original;

          const endDate = dayjs().format('YYYY-MM-DD');
          const startDate = dayjs()
            .subtract(daysInScope, 'days')
            .format('YYYY-MM-DD');

          const url = `/${sectionIdFromFundType(regulation_id)}/fails?fundId=${fund_name}&startDate=${startDate}&endDate=${endDate}`;

          return (
            <Link
              to={url}
              className="flex h-8 w-8 items-center justify-center rounded border border-gray-200 bg-white text-center text-sm text-gray-600 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              title="View detailed compliance information"
            >
              <FaExternalLinkAlt className="h-3 w-3" />
            </Link>
          );
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    autoResetPageIndex: false,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: { fuzzy: fuzzyGlobalFilter },
    sortingFns: { customSortingFn: undefined as unknown as SortingFn<any> },
    globalFilterFn: 'fuzzy',
    manualPagination: false,
  });

  // Smart pagination: keep current page unless it exceeds the last available page
  useEffect(() => {
    const totalRows = table.getFilteredRowModel().rows.length;
    const size = table.getState().pagination.pageSize;
    const maxPageIndex = Math.max(
      0,
      Math.ceil(totalRows / Math.max(1, size)) - 1,
    );
    const currentIndex = table.getState().pagination.pageIndex;
    if (currentIndex > maxPageIndex) {
      setPagination((prev) => ({ ...prev, pageIndex: maxPageIndex }));
    }
  }, [
    table,
    table.getFilteredRowModel().rows.length,
    table.getState().pagination.pageSize,
  ]);

  return (
    <div className="flex h-full w-full flex-col" ref={containerRef}>
      {/* Table */}
      <div className="flex min-h-0 flex-1 flex-col justify-between overflow-hidden rounded border-2 bg-white">
        {/* Single table with sticky header */}
        <div
          className="relative min-h-0 flex-1 overflow-auto bg-white"
          ref={bodyRef}
          style={
            table.getPaginationRowModel().rows.length > 0 &&
            bodyHeightRef.current
              ? { height: `${bodyHeightRef.current}px` }
              : undefined
          }
        >
          <table className="min-w-full table-fixed bg-white text-sm">
            <thead className="sticky top-0 z-10 bg-gray-100">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`group whitespace-nowrap px-3 py-1.5 font-semibold text-gray-700 transition ${
                        [
                          'fail_count',
                          'alert_count',
                          'pass_count',
                          'total_runs',
                          'fail_rate_percent',
                          'alert_rate_percent',
                          'pass_rate_percent',
                        ].includes(
                          header.column.id ||
                            ((header.column.columnDef as any)
                              .accessorKey as string),
                        )
                          ? 'text-right'
                          : (header.column.columnDef as any).accessorKey ===
                              'more_info'
                            ? 'text-center'
                            : 'text-left'
                      }`}
                      style={{ width: `${header.getSize()}px` }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div
                        className={`flex cursor-pointer items-center gap-1 ${
                          [
                            'fail_count',
                            'alert_count',
                            'pass_count',
                            'total_runs',
                            'fail_rate_percent',
                            'alert_rate_percent',
                            'pass_rate_percent',
                          ].includes(
                            header.column.id ||
                              ((header.column.columnDef as any)
                                .accessorKey as string),
                          )
                            ? 'justify-end'
                            : (header.column.columnDef as any).accessorKey ===
                                'more_info'
                              ? 'justify-center'
                              : ''
                        }`}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        <span className="ml-1">
                          {header.column.getIsSorted() === 'asc' ? (
                            <ArrowDropUp className="size-4 text-gray-600 group-hover:fill-blue-700" />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <ArrowDropDown className="size-4 text-gray-600 group-hover:fill-blue-700" />
                          ) : (
                            <SortIcon className="size-4 text-gray-600 group-hover:fill-blue-700" />
                          )}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getPaginationRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="group h-[41px] border-b align-middle odd:bg-white even:bg-gray-50 hover:bg-blue-50"
                  onMouseEnter={() => setHoveredFundId(row.original.fund_name)}
                  onMouseLeave={() => setHoveredFundId(null)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isNumericColumn = [
                      'fail_count',
                      'alert_count',
                      'pass_count',
                      'total_runs',
                      'fail_rate_percent',
                      'alert_rate_percent',
                      'pass_rate_percent',
                    ].includes(
                      cell.column.id ||
                        ((cell.column.columnDef as any).accessorKey as string),
                    );

                    const isFailDetailsColumn =
                      (cell.column.columnDef as any).accessorKey ===
                      'more_info';

                    return (
                      <td
                        key={cell.id}
                        className={`h-[41px] whitespace-nowrap px-4 py-0 align-middle text-gray-800 group-hover:text-blue-700 ${
                          isNumericColumn
                            ? 'text-right'
                            : isFailDetailsColumn
                              ? 'text-center'
                              : ''
                        }`}
                        style={{ width: `${cell.column.getSize()}px` }}
                      >
                        <div
                          className={`flex h-[41px] items-center overflow-hidden ${
                            isNumericColumn
                              ? 'justify-end pr-5'
                              : isFailDetailsColumn
                                ? 'justify-center pr-5'
                                : ''
                          }`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* Always fill up to current pageSize with placeholders for consistent height */}
              {Array.from({
                length: Math.max(
                  0,
                  pagination.pageSize -
                    table.getPaginationRowModel().rows.length,
                ),
              }).map((_, index) => (
                <tr key={`empty-${index}`} className="border-b bg-white">
                  <td
                    colSpan={columns.length}
                    className="h-[41px] px-4 py-0 leading-none text-gray-400"
                  >
                    &nbsp;
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {table.getFilteredRowModel().rows.length === 0 && (
            <div
              className="pointer-events-none absolute left-0 right-0 flex items-center justify-center"
              style={{ top: `${headerHeightRef.current}px`, bottom: 0 }}
            >
              <div className="rounded bg-white/90 px-4 py-1 text-center text-gray-500">
                No results found.
              </div>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div
          className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm"
          ref={paginationRef}
        >
          <span>Results: {table.getFilteredRowModel().rows.length}</span>
          <span>
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </span>

          <div className="flex gap-1">
            {(
              [
                ['⏮', () => table.setPageIndex(0), table.getCanPreviousPage()],
                ['◀', () => table.previousPage(), table.getCanPreviousPage()],
                ['▶', () => table.nextPage(), table.getCanNextPage()],
                [
                  '⏭',
                  () => table.setPageIndex(table.getPageCount() - 1),
                  table.getCanNextPage(),
                ],
              ] as [string, () => void, boolean][]
            ).map(([label, action, enabled], i) => (
              <button
                key={i}
                onClick={action}
                disabled={!enabled}
                className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 disabled:opacity-30"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
